import { FlagIcon, LogoutIcon, SettingsIcon, UserIcon } from "@cynthai/icons";
import { Divider } from "@cynthai/ui";
import { Link as RouterLink } from "@tanstack/react-router";
import { useState } from "react";

import { useAppCapabilities } from "@/core/hooks/use-app-capabilities";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { m } from "@/paraglide/messages";

import { SidebarSupportMenu } from "./SidebarSupportMenu";

type SidebarProfileMenuProps = {
  canManageHelpCenter: boolean;
  organizationId: string | null | undefined;
  userId: string;
  closeProfileMenu: () => void;
};

export function SidebarProfileMenu({
  canManageHelpCenter,
  organizationId,
  userId,
  closeProfileMenu,
}: Readonly<SidebarProfileMenuProps>) {
  const [isSupportMenuOpen, setIsSupportMenuOpen] = useState(false);
  const logout = useLogout();
  const appCapabilities = useAppCapabilities();

  return (
    <div className="flex flex-col gap-1 outline-none">
      <RouterLink
        to="/settings"
        onClick={closeProfileMenu}
        className="text-text-primary hover:bg-secondary flex h-10 items-center gap-3 rounded-lg px-2 text-base"
      >
        <SettingsIcon width={20} height={20} className="shrink-0" />
        {m.account_settings()}
      </RouterLink>
      {organizationId ? (
        <RouterLink
          to="/organization/$orgId/user/$userId"
          params={{ orgId: organizationId, userId }}
          onClick={closeProfileMenu}
          className="text-text-primary hover:bg-secondary flex h-10 items-center gap-3 rounded-lg px-2 text-base"
        >
          <UserIcon width={20} height={20} className="shrink-0" />
          {m.my_profile()}
        </RouterLink>
      ) : null}
      <Divider />
      {canManageHelpCenter ? (
        <SidebarSupportMenu
          variant="admin"
          isOpen={isSupportMenuOpen}
          onOpenChange={setIsSupportMenuOpen}
          closeProfileMenu={closeProfileMenu}
        />
      ) : (
        <SidebarSupportMenu
          variant="normal"
          closeProfileMenu={closeProfileMenu}
        />
      )}
      {appCapabilities.bug_reports ? (
        <RouterLink
          to="/report-issue"
          onClick={closeProfileMenu}
          className="text-text-primary hover:bg-secondary flex h-10 items-center gap-3 rounded-lg px-2 text-base"
        >
          <FlagIcon width={20} height={20} />
          {m.report_an_issue()}
        </RouterLink>
      ) : null}
      <Divider />
      <button
        type="button"
        className="text-text-primary hover:bg-secondary flex h-10 items-center gap-3 rounded-lg px-2 text-base"
        onClick={() => {
          closeProfileMenu();
          logout.mutate({});
        }}
      >
        <LogoutIcon width={20} height={20} />
        {m.sign_out()}
      </button>
    </div>
  );
}
